import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

// Use environment variable or hardcode the full API URL
// Modify to match the current hostname - could be localhost in development or actual domain in production
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

// Debug logging - set to false in production
const PRINT_DEBUG = false;
const logDebug = (...args) => PRINT_DEBUG && console.log(...args);

// Create the permission context
const PermissionContext = createContext(null);

// Dynamic permission lists - these will be populated from the database
let DYNAMIC_DASHBOARD_PERMISSIONS = [];
let DYNAMIC_MENU_PERMISSIONS = [];

// Define the provider component
export const PermissionProvider = ({ children }) => {
  const { currentUser, isAuthenticated, logout } = useAuth();
  // Initialize with empty permissions array - will be populated from API
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading state while fetching permissions
  const [error, setError] = useState(null);

  // Fetch user permissions from the server
  const fetchUserPermissions = async () => {
    try {
      logDebug('🔒 PermissionContext: Starting permission fetch');
      setLoading(true);
      setError(null);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        logDebug('🔒 PermissionContext: No token found, setting empty permissions');
        setUserPermissions([]);
        setLoading(false);
        return;
      }
      
      // Check if this is a kiosk token and handle it specially
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.type === 'kiosk') {
          // Per-user training-kiosk sessions are locked down to training only —
          // anything else they try to URL-hack into hits PermissionRoute and bounces to /login.
          let kioskUser = {};
          try {
            kioskUser = JSON.parse(localStorage.getItem('user') || '{}');
          } catch (_) { kioskUser = {}; }

          if (kioskUser.trainingKioskUser) {
            console.log('🔧 PermissionContext: Training-kiosk user session, granting only ksp_training');
            setUserPermissions(['ksp_training']);
          } else {
            console.log('🔧 PermissionContext: Kiosk token detected, granting all admin permissions');
            // Grant all permissions for kiosk tokens (admin level access)
            setUserPermissions([
              'labor_management_system', 'lms_kiosk', 'lms_clock_in', 'lms_hours_stats',
              'lms_profile', 'lms_administration', 'lms_activities', 'lms_business_units',
              'warehouse_metrics', 'truck_scheduler', 'downstocking_report',
              'update_order_due_date', 'sftp_administration', 'view_users', 'manage_roles',
              'ksp_training'
            ]);
          }
          setLoading(false);
          return;
        }
      } catch (tokenError) {
        console.error('Error parsing token for kiosk check:', tokenError);
      }
      
      // For debugging only - decode token to see what's in it
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          logDebug('🔒 PermissionContext: Decoded token payload:', payload);
          logDebug('🔒 PermissionContext: Token roles:', payload.roles);
        }
      } catch (decodeError) {
        logDebug('🔒 PermissionContext: Error decoding token:', decodeError);
      }
      
      logDebug('🔒 PermissionContext: Fetching permissions from API');
      logDebug(`🔒 PermissionContext: API URL: ${API_URL}/permissions/my-permissions`);
      
      // Fetch permissions from server
      const response = await fetch(`${API_URL}/permissions/my-permissions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      logDebug('🔒 PermissionContext: API response status:', response.status);
      logDebug('🔒 PermissionContext: API response headers:', {
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length')
      });
      
      if (!response.ok) {
        // Handle 401 (Unauthorized) - token expired, redirect to login
        if (response.status === 401) {
          console.log('🔒 PermissionContext: Token expired (401), logging out and redirecting to login');
          logout();
          window.location.href = '/login';
          return; // Exit early, don't continue with error handling
        }
        throw new Error(`Failed to fetch permissions: ${response.status} ${response.statusText}`);
      }
      
      const responseText = await response.text();
      logDebug('🔒 PermissionContext: Raw API response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('🔒 PermissionContext: JSON parse error:', parseError);
        throw new Error('Invalid JSON response from server');
      }
      
      logDebug('🔒 PermissionContext: Parsed API response:', data);
      
      // DEBUG: Let's examine what's in data.permissions
      if (data && Array.isArray(data.permissions)) {
        logDebug('🔒 PermissionContext: Got permissions array with length:', data.permissions.length);
        
        // DEBUG: Log the first few permission objects to see their structure
        if (data.permissions.length > 0) {
          logDebug('🔒 PermissionContext: First permission object:', data.permissions[0]);
        }
        
        const permissionCodes = data.permissions.map(p => p.permission_code);
        logDebug('🔒 PermissionContext: Setting user permissions:', permissionCodes);
        logDebug('🔒 PermissionContext: Required dashboard permissions:', [
          'labor_management_system', 'warehouse_metrics', 'truck_scheduler', 
          'downstocking_report', 'update_order_due_date', 'sftp_administration'
        ]);
        
        // Check if user has admin permissions specifically
        const hasAdmin = permissionCodes.some(code => code === 'admin' || code === 'all_permissions');
        logDebug('🔒 PermissionContext: User has admin permission:', hasAdmin);
        
        // Check for dashboard permissions specifically
        const dashboardPermissions = [
          'labor_management_system', 'warehouse_metrics', 'truck_scheduler', 
          'downstocking_report', 'update_order_due_date', 'sftp_administration', 
          'script_manager', 'mysql_management'
        ];
        const userDashboardPermissions = dashboardPermissions.filter(p => permissionCodes.includes(p));
        logDebug('🔒 PermissionContext: User dashboard permissions:', userDashboardPermissions);
        
        setUserPermissions(permissionCodes);
      } else {
        console.error('🔒 PermissionContext: Unexpected data format from API', data);
        logDebug('🔒 PermissionContext: API returned data but no permissions array:', data);
        throw new Error('Unexpected data format from server');
      }
    } catch (err) {
      console.error('🔒 PermissionContext: Error fetching permissions:', err);
      logDebug('🔒 PermissionContext: Error details:', err);
      setError(err.message);
      setUserPermissions([]);
    } finally {
      setLoading(false);
      
      // Clear the page refresh flag once permissions are fully loaded
      // This prevents the special bypass logic from staying active too long
      setTimeout(() => {
        if (sessionStorage.getItem('fromPageRefresh')) {
          console.log('🔒 PermissionContext: Permissions loaded, clearing page refresh flag');
          sessionStorage.removeItem('fromPageRefresh');
        }
      }, 1000);
    }
  };

  // Fetch permissions when user authentication status changes
  useEffect(() => {
    logDebug('🔒 PermissionContext: Auth status changed, isAuthenticated:', isAuthenticated);
    logDebug('🔒 PermissionContext: Current User:', currentUser);
    
    if (isAuthenticated) {
      logDebug('🔒 PermissionContext: User is authenticated, fetching permissions');
      logDebug('🔒 PermissionContext: User roles:', currentUser?.roles);
      
      // Mark that permissions are still loading - this will help PermissionRoute make better decisions
      sessionStorage.setItem('permissions_still_loading', 'true');
      
      // Log local storage data
      try {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        logDebug('🔒 PermissionContext: Stored User:', storedUser);
        logDebug('🔒 PermissionContext: Stored User roles:', storedUser?.roles);
        
        // If this user is an admin based on roles, we can apply some permissions immediately
        // This helps avoid unnecessary redirects during loading
        if (storedUser?.roles && Array.isArray(storedUser.roles)) {
          const isAdmin = storedUser.roles.some(role => role.name === 'Admin');
          if (isAdmin) {
            console.log('🔒 PermissionContext: Admin detected, pre-loading common permissions');
            // Apply some basic permissions right away for admins to prevent redirect issues
            // The actual permissions will be loaded from the API shortly
            setUserPermissions(['manage_roles', 'view_users', 'home_section', 'profile_section']);
            // Still continue with the normal permission fetch for completeness
          }
        }
      } catch (err) {
        logDebug('🔒 PermissionContext: Error parsing stored user:', err);
      }
      
      fetchUserPermissions();
    } else {
      logDebug('🔒 PermissionContext: User is not authenticated, setting empty permissions');
      setUserPermissions([]);
      setLoading(false);
      sessionStorage.removeItem('permissions_still_loading');
    }
  }, [isAuthenticated, currentUser]);

  // Check if the user has a specific permission
  const hasPermission = (permissionId) => {
    if (!isAuthenticated) {
      logDebug(`🔒 PermissionContext: Not authenticated, denying permission '${permissionId}'`);
      return false;
    }
    
    if (!userPermissions || userPermissions.length === 0) {
      logDebug(`🔒 PermissionContext: No permissions loaded, denying permission '${permissionId}'`);
      return false;
    }
    
    // Check for admin permission that grants all access
    if (userPermissions.includes('admin') || userPermissions.includes('all_permissions')) {
      logDebug(`🔒 PermissionContext: User has admin permission, granting '${permissionId}'`);
      return true;
    }
    
    // Check for the specific permission
    const hasSpecificPermission = userPermissions.includes(permissionId);
    logDebug(`🔒 PermissionContext: Permission '${permissionId}' ${hasSpecificPermission ? 'granted' : 'denied'}`);
    return hasSpecificPermission;
  };

  // Check if the user has any of the provided permissions
  const hasAnyPermission = (permissionIds) => {
    if (!isAuthenticated) {
      logDebug(`🔒 PermissionContext: Not authenticated, denying permissions`);
      return false;
    }
    
    if (!userPermissions || userPermissions.length === 0) {
      logDebug(`🔒 PermissionContext: No permissions loaded, denying all permissions`);
      return false;
    }
    
    // Check for admin permission that grants all access
    if (userPermissions.includes('admin') || userPermissions.includes('all_permissions')) {
      logDebug(`🔒 PermissionContext: User has admin permission, granting ANY permissions`);
      return true;
    }
    
    // Check if user has any of the required permissions
    const hasAny = permissionIds.some(id => userPermissions.includes(id));
    logDebug(`🔒 PermissionContext: ANY permissions ${hasAny ? 'granted' : 'denied'}`);
    return hasAny;
  };

  // Check if the user has all of the provided permissions
  const hasAllPermissions = (permissionIds) => {
    if (!isAuthenticated) {
      logDebug(`🔒 PermissionContext: Not authenticated, denying permissions`);
      return false;
    }
    
    if (!userPermissions || userPermissions.length === 0) {
      logDebug(`🔒 PermissionContext: No permissions loaded, denying all permissions`);
      return false;
    }
    
    // Check for admin permission that grants all access
    if (userPermissions.includes('admin') || userPermissions.includes('all_permissions')) {
      logDebug(`🔒 PermissionContext: User has admin permission, granting ALL permissions`);
      return true;
    }
    
    // Check if user has all of the required permissions
    const hasAll = permissionIds.every(id => userPermissions.includes(id));
    logDebug(`🔒 PermissionContext: ALL permissions ${hasAll ? 'granted' : 'denied'}`);
    return hasAll;
  };

  // Refresh permissions
  const refreshPermissions = () => {
    return fetchUserPermissions();
  };

  const value = {
    userPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions,
    loading,
    error
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// Custom hook for using the permission context
export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;